import { css } from '@adornis/chemistry/directives/css.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import { routingState } from '@adornis/router/client/routing-state.js';
import type { XWriter } from '@adornis/writer/x-writer.js';
import { permissionGuard } from '@ils/permissions/client/permissionGuard.js';
import { updateOperatorNotesForSlot } from '@ils/slots/api/slot.js';
import type { ExamSlotGroup } from '@ils/slots/db/examSlotGroup.js';
import type { Slot } from '@ils/slots/db/slot.js';
import { isTextOsceCategory } from '@ils/static/enums.js';
import { html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { DateTime } from 'luxon';
import type { Answer } from '../../db/answer.js';
import type { ExamDay } from '../../db/examdays.js';
import type { ILSUser } from '../../db/ils-user.js';
import type { LivekitRoom } from '../../db/livekit.js';
import type { ILSToken } from '../../db/token.js';
import { ILSCreateFallbackSlot } from './ils-create-fallback-slot.js';
import { ILSEvaluationInspectDialog } from './ils-evaluation-inspect.js';
import { IlsReplaceTesterDialog } from './ils-replace-tester-dialog.js';

/**
 * @element ils-timeline-detail-dialog
 */
@customElement('ils-timeline-detail-dialog')
export class IlsTimelineDetailDialog extends XDialog<void> {
  @property({ attribute: false }) examSlot?: Slot;
  @property({ attribute: false }) user?: ILSUser;
  @property({ attribute: false }) tester?: ILSUser;
  @property({ attribute: false }) token?: ILSToken;
  @property({ attribute: false }) activeRoom?: LivekitRoom;

  @property({ attribute: false }) examDay?: ExamDay;
  @property({ attribute: false }) examSlots?: ExamSlotGroup[];
  @property({ attribute: false }) answers?: Answer[];

  @property({ attribute: false }) serverTime?: Date;

  @property({ attribute: false }) tooFewMembersInActiveRoom?: boolean;
  @property({ attribute: false }) noActiveRoomForActiveSlot?: boolean;

  static override get element_name() {
    return 'ils-timeline-detail-dialog';
  }

  // eslint-disable-next-line complexity
  override render() {
    if (!this.examSlot || !this.user || !this.examDay || !this.examSlots)
      return html` <x-flex padding="md"> <x-text tone="error"> 404 - Slot not found </x-text> </x-flex>`;
    const examSlot = this.examSlot;
    const user = this.user;
    const examDay = this.examDay;
    const examSlots = this.examSlots;
    const answer = this.answers?.find(a => a.slotID === examSlot._id);
    return html`
      <x-flex padding="md" space="lg">
        <x-flex horizontal space-between space="lg">
          <x-h4> ${user.ecommerce?.firstName} ${user.ecommerce?.lastName} </x-h4>
          <x-button mode="icon" icon="close" @click=${() => this.close()}> close </x-button>
        </x-flex>
        <x-flex horizontal crossaxis-center space="sm">
          <ils-osce-type .osce=${examSlot.osce}></ils-osce-type>
          <x-text ellipsis .value=${examSlot.osce?.title} title=${examSlot.osce?.title}> </x-text>
        </x-flex>
        <x-flex space="sm">
          <x-text>
            ${examSlot.startTime.toLocaleString(DateTime.TIME_24_SIMPLE)} -
            ${examSlot.endTime?.toLocaleString(DateTime.TIME_24_SIMPLE) ?? '-'}
          </x-text>
          <x-text>
            ${this.tester
              ? `${this.tester.ecommerce?.firstName} ${this.tester.ecommerce?.lastName}`
              : 'Kein Schauspieler'}
          </x-text>
          <x-text tone="subtle"> Token: ${this.token?.shortToken ?? 'Kein Token'} </x-text>
        </x-flex>
        ${this.activeRoom
          ? html`
              <x-flex horizontal crossaxis-center space="sm">
                <x-icon> person_search </x-icon>
                <x-text> Personen im Gespräch: ${this.activeRoom.participants.length} </x-text>
              </x-flex>
            `
          : html` <x-text> Kein aktiver Raum gefunden </x-text> `}
        ${examSlot.isCandidateMissing ? this._warning('Kandidat wurde als fehlend markiert') : nothing}
        ${this.tooFewMembersInActiveRoom
          ? this._error('Zu wenig Leute im aktiven Slot - Entweder Kandidat oder Prüfer fehlt')
          : nothing}
        ${this.noActiveRoomForActiveSlot
          ? this._error('Kein Raum für den aktiven Slot gefunden - Weder Kandidat noch Prüfer anwesend')
          : nothing}
        <x-flex space="sm">
          ${this._answerDetailButton(answer)}
          ${examDay.fallbackTesters.length
            ? permissionGuard({
                resource: 'slots',
                action: 'usefallbackactor',
                guardedTemplate: html`
                  <x-button
                    mode="outline"
                    icon="find_replace"
                    @click=${async () =>
                      (await IlsReplaceTesterDialog.showPopup<boolean>({
                        props: {
                          examDay,
                          examSlot,
                          allSlots: examSlots.flatMap(examSlotGroup => examSlotGroup.slots),
                        },
                      })) && this.close()}
                  >
                    Ersatzschauspieler einsetzen
                  </x-button>
                `,
              })
            : nothing}
          ${this.examSlot.problemWasReported
            ? html`
                <x-flex space="sm" horizontal>
                  <x-flex flex> ${this._error('Problem gemeldet!')} </x-flex>
                  <x-button
                    mode="outline"
                    icon="check"
                    @click=${async () => {
                      const confirm = await XDialog.confirm(
                        'Hier wurde ein Problem gemeldet. Wurde es bereits gelöst?',
                        {
                          highlightDecline: true,
                        },
                      );
                      if (confirm) {
                        examSlot.problemWasReported = false;
                        await XDialog.waitFor(examSlot.save());
                        this.requestUpdate();
                      }
                    }}
                  >
                    Problem gelöst
                  </x-button>
                </x-flex>
              `
            : nothing}
          ${this.examSlot.osce?.taskType === 'voice' &&
          examSlot.endTime < DateTime.now() &&
          !examSlot.isAudioLongEnough &&
          answer?.has('file')
            ? this._warning('Die hochgeladene Audio-Datei ist zu kurz')
            : nothing}
          ${permissionGuard({
            resource: 'slots',
            action: 'createfallback',
            guardedTemplate: html`
              <x-button
                flex
                mode="outline"
                icon="refresh"
                ?disabled=${examSlot.fallbackSlotID}
                @click=${async () =>
                  (await ILSCreateFallbackSlot.showPopup<boolean>({
                    props: {
                      examDay: this.examDay,
                      examSlots: this.examSlots,
                      failedSlot: this.examSlot,
                    },
                  })) && this.close()}
              >
                Fallback anlegen
              </x-button>
            `,
          })}
          ${permissionGuard({
            resource: 'slots',
            action: 'spectate',
            guardedTemplate: html`
              <x-button
                flex
                icon="person_search"
                href="/start/spectate/${this.examSlot._id}?${routingState.searchParams.getValue().toString()}"
              >
                In laufenden Slot reinhorchen
              </x-button>
            `,
          })}
          ${permissionGuard({
            resource: 'slots',
            action: 'modifycomment',
            guardedTemplate: html`
              <x-flex space="sm">
                <x-writer placeholder="Notiz" id="operatorNotes" .value=${this.examSlot.operatorNotes}></x-writer>
                <x-button
                  icon="comment"
                  mode="outline"
                  @click=${async () => {
                    // TODO show this to the inspectors during rating
                    const writer = this.shadowRoot?.getElementById('operatorNotes') as XWriter | undefined;
                    if (!writer) return;
                    const notes = writer.value.value;
                    await updateOperatorNotesForSlot(examSlot._id, notes)();
                    void XSnackbar.show('Notiz gespeichert');
                  }}
                >
                  Notiz speichern
                </x-button>
              </x-flex>
            `,
          })}
        </x-flex>
      </x-flex>
    `;
  }

  private _answerDetailButton(answer: Answer | undefined): Renderable {
    if (answer)
      return permissionGuard({
        resource: 'ratings',
        action: 'viewothers',
        guardedTemplate: html`
          <x-button
            icon="check"
            tone=${answer.isValid() ? '' : 'warning'}
            @click=${() =>
              ILSEvaluationInspectDialog.showPopup({
                props: { slotID: this.examSlot!._id },
              })}
          >
            Antwort ansehen
          </x-button>
        `,
      });
    if (
      isTextOsceCategory(this.examSlot?.osce?.osceType) ||
      (this.examSlot?.endTime ?? -Infinity) > DateTime.fromJSDate(this.serverTime ?? new Date())
    )
      return nothing;
    return permissionGuard({
      resource: 'answers',
      action: 'createempty',
      guardedTemplate: html`
        <x-button
          icon="warning"
          tone="error"
          @click=${() => {
            ILSEvaluationInspectDialog.showPopup({
              props: { slotID: this.examSlot!._id, editable: true },
            });
          }}
        >
          Antwort ansehen
        </x-button>
      `,
      forbiddenTemplate: this._error('Keine Antwort hochgeladen'),
    });
  }

  private _error(msg: string) {
    return html`
      <x-flex
        horizontal
        crossaxis-center
        space="md"
        padding="sm"
        ${css({ background: this.colors.tone.error.shade(100), color: this.colors.tone.error })}
      >
        <x-icon> warning </x-icon>
        <x-text bold> ${msg} </x-text>
      </x-flex>
    `;
  }

  private _warning(msg: string) {
    return html`
      <x-flex
        horizontal
        crossaxis-center
        space="md"
        padding="sm"
        ${css({ background: this.colors.tone.warning.shade(50), color: this.colors.tone.warning })}
      >
        <x-icon> warning </x-icon>
        <x-text bold> ${msg} </x-text>
      </x-flex>
    `;
  }
}
